import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Checkbox,
  FormControlLabel,
  SelectChangeEvent,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Save as SaveIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { EarlyCareerTalent, RECOMMENDED_PLACEMENTS, EARLY_CAREER_PROGRAMS } from '../types';

interface EarlyCareerTalentSectionProps {
  selectedAlias?: string;
}

const EarlyCareerTalentSection: React.FC<EarlyCareerTalentSectionProps> = ({ selectedAlias }) => {
  const [formData, setFormData] = useState<Omit<EarlyCareerTalent, 'id' | 'alias' | 'manager_alias'>>({
    eligible_to_progress_to_field: null,
    residency_progression_readiness_date: null,
    actual_residency_progression_date: null,
    recommended_placement: null,
    early_career_program: null,
    interesting: false,
    ect_start_date: null,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedAlias) {
      fetchEarlyCareerTalent();
    }
  }, [selectedAlias]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEarlyCareerTalent = async () => {
    if (!selectedAlias) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getEarlyCareerTalent(selectedAlias);
      setFormData({
        eligible_to_progress_to_field: data.eligible_to_progress_to_field,
        residency_progression_readiness_date: data.residency_progression_readiness_date,
        actual_residency_progression_date: data.actual_residency_progression_date,
        recommended_placement: data.recommended_placement,
        early_career_program: data.early_career_program,
        interesting: data.interesting,
        ect_start_date: data.ect_start_date,
      });
    } catch (err: any) {
      if (err.message?.includes('404')) {
        setFormData({
          eligible_to_progress_to_field: null,
          residency_progression_readiness_date: null,
          actual_residency_progression_date: null,
          recommended_placement: null,
          early_career_program: null,
          interesting: false,
          ect_start_date: null,
        });
      } else {
        setError('Error loading early career talent data.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChange = (field: keyof typeof formData) => (event: SelectChangeEvent<string>) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value || null }));
    setSaveSuccess(false);
  };

  const handleDateChange = (field: keyof typeof formData) => (value: Date | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value ? value.toISOString().split('T')[0] : null,
    }));
    setSaveSuccess(false);
  };

  const handleCheckboxChange = (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.checked }));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!selectedAlias) return;
    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    try {
      await api.saveEarlyCareerTalent(selectedAlias, formData);
      setSaveSuccess(true);
    } catch (err) {
      setError('Error saving early career talent data. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!selectedAlias) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h2" gutterBottom>
          Early Career Talent
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a direct report to view their early career talent record.
        </Typography>
      </Paper>
    );
  }

  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h2" gutterBottom>
          Early Career Talent
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading early career talent data...
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h2">Early Career Talent</Typography>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            size="small"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Early career talent data saved successfully.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={3}>
          <FormControl fullWidth>
            <InputLabel>Eligible to Progress to Field</InputLabel>
            <Select
              value={formData.eligible_to_progress_to_field || ''}
              label="Eligible to Progress to Field"
              onChange={handleSelectChange('eligible_to_progress_to_field')}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </FormControl>

          <DatePicker
            label="Residency Progression Readiness Date"
            value={formData.residency_progression_readiness_date ? new Date(formData.residency_progression_readiness_date + 'T00:00:00') : null}
            onChange={handleDateChange('residency_progression_readiness_date')}
            slotProps={{ textField: { fullWidth: true } }}
          />

          <DatePicker
            label="Actual Residency Progression Date"
            value={formData.actual_residency_progression_date ? new Date(formData.actual_residency_progression_date + 'T00:00:00') : null}
            onChange={handleDateChange('actual_residency_progression_date')}
            slotProps={{ textField: { fullWidth: true } }}
          />

          <FormControl fullWidth>
            <InputLabel>Recommended Placement</InputLabel>
            <Select
              value={formData.recommended_placement || ''}
              label="Recommended Placement"
              onChange={handleSelectChange('recommended_placement')}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {RECOMMENDED_PLACEMENTS.map((val) => (
                <MenuItem key={val} value={val}>{val}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Early Career Program</InputLabel>
            <Select
              value={formData.early_career_program || ''}
              label="Early Career Program"
              onChange={handleSelectChange('early_career_program')}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {EARLY_CAREER_PROGRAMS.map((val) => (
                <MenuItem key={val} value={val}>{val}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.interesting}
                onChange={handleCheckboxChange('interesting')}
              />
            }
            label="Interesting"
          />

          <DatePicker
            label="ECT Start Date"
            value={formData.ect_start_date ? new Date(formData.ect_start_date + 'T00:00:00') : null}
            onChange={handleDateChange('ect_start_date')}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Box>
      </Paper>
    </LocalizationProvider>
  );
};

export default EarlyCareerTalentSection;
